import { useEffect, useState } from 'react';
import type { UserRolle } from '@hrh/shared';

import { clearToken, getToken } from '@/lib/api-client.js';

interface AuthUser {
  id: string;
  email: string;
  rolle: UserRolle;
  fahrerId: string | null;
}

interface UseAuthResult {
  user: AuthUser | null;
  isLoading: boolean;
  logout: () => void;
}

function decodeJwtPayload(token: string): AuthUser | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payloadPart = parts[1];
  if (!payloadPart) return null;
  try {
    const decoded = JSON.parse(atob(payloadPart)) as {
      sub: string;
      email: string;
      rolle: UserRolle;
      fahrerId: string | null;
      exp: number;
    };
    if (decoded.exp * 1000 < Date.now()) return null;
    return {
      id: decoded.sub,
      email: decoded.email,
      rolle: decoded.rolle,
      fahrerId: decoded.fahrerId,
    };
  } catch {
    return null;
  }
}

export function useAuth(): UseAuthResult {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (token) {
      const decoded = decodeJwtPayload(token);
      if (decoded) {
        setUser(decoded);
      } else {
        clearToken();
      }
    }
    setIsLoading(false);
  }, []);

  const logout = (): void => {
    clearToken();
    setUser(null);
  };

  return { user, isLoading, logout };
}
